// attributionPublic.js
// Public API: both functions accept an ImageMetadata-like object only.
// Internally manages cheerio via a cached dynamic import (no cheerio arg required).
//
// Exports:
//   - async function getAttributionText(imd) -> string
//   - async function getAttributionURLs(imd) -> { photoUrl, artistUrl, licenseUrl }
//
// Both are null-safe and tolerant of many input shapes.

let _cheerioLoad = null; // cached cheerio.load function (or null until loaded)
let _cheerioLoadingPromise = null;

async function _ensureCheerioLoad() {
    if (_cheerioLoad) return _cheerioLoad;
    if (_cheerioLoadingPromise) return _cheerioLoadingPromise;

    _cheerioLoadingPromise = (async () => {
        const mod = await import("cheerio");
        // mod.load or mod.default.load depending on bundler
        const loadFn = (mod && (mod.load ?? (mod.default && mod.default.load))) || null;
        _cheerioLoad = loadFn;
        return _cheerioLoad;
    })();

    return _cheerioLoadingPromise;
}

function _escapeText(str) {
    if (str == null) return "";
    return String(str).trim();
}

function _regexParseFirstLink(html) {
    if (!html) return { href: null, text: null };
    try {
        const aMatch = html.match(/<a\b[^>]*href=(['"]?)((?:\\\1|.)*?)\1[^>]*>(.*?)<\/a>/i);
        if (!aMatch) return { href: null, text: null };
        const hrefRaw = aMatch[2] || "";
        const innerHtml = aMatch[3] || "";
        const text = (innerHtml || "").replace(/<[^>]*>/g, "").trim() || null;
        const href = hrefRaw ? hrefRaw.trim() : null;
        return { href: href || null, text: text || null };
    } catch {
        return { href: null, text: null };
    }
}

/**
 * Parse first link from given HTML string.
 * Tries to use cheerioLoad (if provided). If cheerioLoad is null, falls back to regex.
 * Returns { href: string|null, text: string|null }.
 */
function _parseFirstLinkWithCheerioFallback(html, cheerioLoad) {
    if (!html) return { href: null, text: null };

    if (typeof cheerioLoad === "function") {
        try {
            const $ = cheerioLoad(html);
            // prefer "a.external.text" anchors (as seen in some Wikimedia snippets)
            let a = $("a.external.text").first();
            if (!a || a.length === 0) a = $("a").first();
            if (a && a.length > 0) {
                const href = a.attr && a.attr("href") ? String(a.attr("href")).trim() : null;

                // prefer first non-empty text node inside anchor
                let text = null;
                try {
                    const textNode = a
                        .contents()
                        .filter((i, node) => node && node.type === "text" && node.nodeValue && node.nodeValue.trim() !== "")
                        .first();
                    if (textNode && textNode.text) text = textNode.text().trim();
                } catch {
                    // ignore
                }

                if (!text) {
                    text = (a.text && a.text().trim()) || null;
                }

                return { href: href || null, text: text || null };
            }
        } catch {
            // fall through to regex
        }
    }

    // regex fallback
    return _regexParseFirstLink(html);
}

/**
 * getAttributionText(imd)
 * - Returns a plain-text attribution string suitable for Pinterest descriptions.
 * - Always manages cheerio import internally (no external dependency required).
 * - If imd.attributionRequired is falsy, returns an empty string.
 *
 * Format (parts omitted if missing):
 *   "Photo by {artistText} / used under {licenseText} / Cropped from original"
 */
export async function getAttributionText(imd) {



    if (!imd || !imd.attributionRequired) return "";

    // attempt to get cheerio.load internally (may be null if import fails)
    let cheerioLoad = null;
    try {
        cheerioLoad = await _ensureCheerioLoad();
    } catch {
        // ignore — we'll fall back to regex parsing
        cheerioLoad = null;
    }

    // Credit: we show "Photo" as the word for credit (consistent with your UI)
    //const creditHtml = imd.credit || "";
    //const creditInfo = _parseFirstLinkWithCheerioFallback(creditHtml, cheerioLoad);
    const creditText = "Photo";

    // Artist
    const artistHtml = imd.artist || "";
    const artistInfo = _parseFirstLinkWithCheerioFallback(artistHtml, cheerioLoad);
    let artistText = artistInfo.text || null;
    if (!artistText) {
        // fallback: strip tags from artistHtml and use any remaining text
        const plain = (artistHtml || "").replace(/<[^>]*>/g, "").trim();
        artistText = plain || null;
    }

    // License
    const license = imd.license ?? null;
    let licenseText = null;
    if (license) {
        licenseText = _escapeText(license.shortName || license.name || "");
        if (!licenseText && license.url) {
            try {
                const u = new URL(String(license.url));
                licenseText = u.hostname;
            } catch {
                licenseText = String(license.url).trim();
            }
        }
        if (!licenseText) licenseText = null;
    }

    // Build pieces
    const parts = [];
    parts.push(creditText); // always present
    if (artistText) parts.push("by " + artistText);
    if (licenseText) parts.push("/ used under " + licenseText);
    parts.push("/ Cropped from original");

    // join with spaces (license piece already contains '/')
    return parts.join(" ");
}

/**
 * getAttributionURLs(imd)
 * - Returns an object with discovered URLs: { photoUrl, artistUrl, licenseUrl }
 * - All cheerio management is internal; returns nulls when not present.
 */
export async function getAttributionURLs(imd) {
    if (!imd || !imd.attributionRequired) {
        return { photoUrl: null, artistUrl: null, licenseUrl: null };
    }

    let cheerioLoad = null;
    try {
        cheerioLoad = await _ensureCheerioLoad();
    } catch {
        cheerioLoad = null;
    }

    const creditHtml = imd.credit || "";
    const artistHtml = imd.artist || "";
    const creditInfo = _parseFirstLinkWithCheerioFallback(creditHtml, cheerioLoad);
    const artistInfo = _parseFirstLinkWithCheerioFallback(artistHtml, cheerioLoad);
    const photoUrl = creditInfo.href || null;
    const artistUrl = artistInfo.href || null;
    const licenseUrl = imd.license && imd.license.url ? String(imd.license.url).trim() : null;

    return { photoUrl, artistUrl, licenseUrl };
}
